import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/format";
import { Loader2, Download, DatabaseBackup, Archive } from "lucide-react";

async function downloadBlob(url, filename) {
  const res = await api.get(url, { responseType: "blob" });
  const u = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = u;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(u);
}

export default function BackupPanel() {
  const [colls, setColls] = useState([]);
  const [autos, setAutos] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/backup/collections").then((r) => setColls(r.data)).catch(() => {});
    api.get("/backup/auto").then((r) => setAutos(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const full = async () => {
    setBusy(true);
    try {
      await downloadBlob("/backup", `tms_backup_${new Date().toISOString().slice(0, 10)}.zip`);
      toast.success("Backup completo scaricato");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const snapshot = async () => {
    setBusy(true);
    try {
      await api.post("/backup/auto");
      toast.success("Snapshot salvato nello storico");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const totalDocs = colls.reduce((s, c) => s + c.count, 0);

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4" data-testid="backup-panel">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="text-sm font-medium flex items-center gap-2"><DatabaseBackup className="h-4 w-4 text-sky-600" />Backup del database</div>
          <div className="text-xs text-slate-500 mt-1">
            Esporta <b>tutte</b> le collezioni ({colls.length} collezioni, {totalDocs.toLocaleString("it-IT")} record) in un archivio ZIP di file JSON.
            Un backup automatico viene salvato ogni domenica alle 03:00; vengono conservati gli ultimi 8.
          </div>
          <div className="text-xs text-slate-400 mt-1" data-testid="backup-collections">{colls.map((c) => `${c.name} (${c.count})`).join(" · ")}</div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={snapshot} disabled={busy} data-testid="backup-snapshot"><Archive className="h-4 w-4 mr-1.5" />Salva snapshot</Button>
          <Button size="sm" onClick={full} disabled={busy} data-testid="backup-full">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}Scarica backup completo</Button>
        </div>
      </div>
      <div className="border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-xs" data-testid="backup-auto-table">
          <thead className="bg-slate-50 text-slate-500 uppercase"><tr><th className="text-left px-3 py-1.5">Data</th><th className="text-left px-3 py-1.5">Origine</th><th className="text-right px-3 py-1.5">Collezioni</th><th className="text-right px-3 py-1.5">Dimensione</th><th className="px-3"></th></tr></thead>
          <tbody>
            {autos.length === 0 && <tr><td colSpan={5} className="px-3 py-4 text-center text-slate-400">Nessun backup automatico salvato finora</td></tr>}
            {autos.map((b) => (
              <tr key={b.id} className="border-t border-slate-100">
                <td className="px-3 py-1.5">{formatDateTime(b.created_at)}</td>
                <td className="px-3 py-1.5 capitalize">{b.origine}</td>
                <td className="px-3 py-1.5 text-right">{Object.keys(b.collections || {}).length}</td>
                <td className="px-3 py-1.5 text-right">{(b.size / 1024).toFixed(0)} KB</td>
                <td className="px-3 py-1.5 text-right"><Button variant="ghost" size="sm" className="h-7" onClick={() => downloadBlob(`/backup/auto/${b.id}`, `tms_backup_auto_${b.created_at.slice(0, 10)}.zip`)} data-testid={`backup-auto-dl-${b.id}`}><Download className="h-3.5 w-3.5 mr-1" />Scarica</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
